/**
 * @file NotificationsDrawer.tsx
 * @brief Simple notifications drawer with live data from hooks
 */

import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { X, Bell, CheckCheck, Circle, Trash2 } from 'lucide-react';
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead, useClearReadNotifications } from '@/hooks/useNotifications';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNavigate } from 'react-router-dom';

dayjs.extend(relativeTime);

interface NotificationsDrawerProps {
  open: boolean;
  onClose: () => void;
}

const NotificationsDrawer: React.FC<NotificationsDrawerProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { data: notificationsData, isLoading, error } = useNotifications(1, 20);
  const markReadMutation = useMarkNotificationRead();
  const markAllReadMutation = useMarkAllNotificationsRead();
  const clearReadMutation = useClearReadNotifications();

  const notifications = notificationsData?.items || [];

  const handleMarkRead = (notificationId: string) => {
    markReadMutation.mutate(notificationId);
  };

  const handleMarkAllRead = () => {
    markAllReadMutation.mutate();
  };

  const handleClearRead = () => {
    clearReadMutation.mutate();
  };

  const getNotificationIcon = (type: string) => {
    // Simple icon mapping - can be expanded
    return <Bell size={20} />;
  };

  const getNotificationColor = (priority?: string) => {
    switch (priority) {
      case 'high':
        return '#ef4444';
      case 'medium':
        return '#f59e0b';
      case 'low':
        return '#4F46E5';
      default:
        return '#6b7280';
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {notifications.length > 0 && (
              <>
                {/* Desktop: Buttons with text */}
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<CheckCheck size={16} />}
                    onClick={handleMarkAllRead}
                    disabled={markAllReadMutation.isPending}
                  >
                    Mark all read
                  </Button>
                  {notifications.some(n => (n as any).isRead) && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Trash2 size={16} />}
                      onClick={handleClearRead}
                      disabled={clearReadMutation.isPending}
                    >
                      Clear read
                    </Button>
                  )}
                </Box>
                
                {/* Mobile: Icon buttons only */}
                <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5 }}>
                  <IconButton 
                    size="small" 
                    onClick={handleMarkAllRead}
                    disabled={markAllReadMutation.isPending}
                    title="Mark all read"
                  >
                    <CheckCheck size={18} />
                  </IconButton>
                  {notifications.some(n => (n as any).isRead) && (
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={handleClearRead}
                      disabled={clearReadMutation.isPending}
                      title="Clear read"
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  )}
                </Box>
              </>
            )}
            <IconButton size="small" onClick={onClose}>
              <X size={20} />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">Failed to load notifications</Alert>
            </Box>
          ) : notifications.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                px: 3,
                textAlign: 'center',
              }}
            >
              <Bell size={48} strokeWidth={1} style={{ color: '#d1d5db', marginBottom: 16 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No notifications yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We'll notify you when something important happens
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map((notification: any, index: number) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.selected',
                      },
                      position: 'relative',
                    }}
                    onClick={() => {
                      if (!notification.isRead) {
                        handleMarkRead(notification.id);
                      }
                      if (notification.linkType === 'ticket') {
                        onClose();
                        navigate(notification.linkId ? `/tickets?open=${notification.linkId}` : '/tickets');
                      }
                    }}
                  >
                    {!notification.isRead && (
                      <Circle
                        size={8}
                        fill={getNotificationColor(notification.priority)}
                        color={getNotificationColor(notification.priority)}
                        style={{
                          position: 'absolute',
                          left: 8,
                          top: 24,
                        }}
                      />
                    )}
                    <ListItemAvatar sx={{ ml: !notification.isRead ? 2 : 0 }}>
                      <Avatar
                        sx={{
                          bgcolor: `${getNotificationColor(notification.priority)}22`,
                          color: getNotificationColor(notification.priority),
                          width: 40,
                          height: 40,
                        }}
                      >
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: notification.isRead ? 400 : 600 }}>
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
                          >
                            {notification.message}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}
                          >
                            {dayjs(notification.createdAt).fromNow()}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < notifications.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default NotificationsDrawer;
